import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Modal,
  Button,
  Divider,
  Card,
  Spin,
  Input,
  Tabs,
  Form,
  Row,
  Col,
  Select,
  DatePicker,
  Checkbox,
  Empty,
  message,
  Skeleton
} from "antd";
import {
  SearchOutlined,
  MailOutlined,
  AreaChartOutlined,
  LoadingOutlined,
  EyeFilled,
  FileOutlined,
  PictureOutlined,
  BookFilled,
  DownOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as TrustedDealerService from "../../services/TrustedDealer";
import PagesHeader from "../../components/PageHeader";
import ButtonBar from "../../components/ButtonNavigation";
import * as UserDataService from '../../services/GetUserData';
import * as Actions from '../../store/actions/user';
import * as InspectionServices from "../../services/InspectionReports";
import { IconButton } from "../../components/Buttons/buttons";
import CommentSection from "../../components/CommentSection";
import moment from "moment";

const { Option } = Select;

function RequestedInspectionsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Data & Pagination state
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // UI & Filters state
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [inquiriesLoading, setInquiriesLoading] = useState(true);
  const [inquiriesCount, setInquiriesCount] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [statusValues, setStatusValues] = useState({});
  const [searchedText, setSearchedText] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [checkedins, setCheckedins] = useState(true);
  const [checkedass, setCheckedass] = useState(true);
  const [commentData, setCommentData] = useState([]);
  const [filter, setFilter] = useState('all');
  const [subTab, setSubTab] = useState('active');

  const currentUser = useSelector((state) => state.User?.userDetails || state.user?.userData);
  const profileId = currentUser?.inspector_id;

  // Fetch user data once on mount
  useEffect(() => {
    dispatch(UserDataService.getUserData())
      .then((res) => {
        dispatch(Actions.set_user_auth(res));
      })
      .catch(() => {});
  }, [dispatch]);

  // Fetch requested inspections on dependency changes
  useEffect(() => {
    const dealerId = currentUser?.id || currentUser?.inspector_id || profileId || 0;
    if (dealerId) {
      fetchData(dealerId, page, pageSize);
      fetchInquiriesCount(dealerId);
    }
  }, [currentUser, page, pageSize, searchedText, filterStartDate, filterEndDate, filter, subTab]);

  const fetchData = (dealerId, currentPage, currentPageSize) => {
    if (!dealerId) return;
    setLoading(true);

    let statusForApi = "";
    let excludeArchivedForApi = false;

    if (subTab === "active") {
      excludeArchivedForApi = true;
      statusForApi = filter !== "all" ? filter : "";
    } else if (subTab === "completed") {
      statusForApi = "Completed";
    } else if (subTab === "archived") {
      statusForApi = "Archived";
    }

    const filters = {
      page: currentPage,
      pageSize: currentPageSize,
      search: searchedText?.trim() || "",
      date_from: filterStartDate || "",
      date_to: filterEndDate || "",
      status: statusForApi,
      exclude_archived: excludeArchivedForApi
    };
    dispatch(TrustedDealerService.getDealerRequestedInspections(dealerId, filters))
      .then((res) => {
        setData(res?.data || []);
        setTotal(res?.pagination?.total_items || 0);
        setLoading(false);
      })
      .catch(() => {
        setData([]);
        setLoading(false);
      });
  };

  const fetchInquiriesCount = (dealerId = 0) => {
    setInquiriesLoading(true);
    dispatch(TrustedDealerService.getDealerInquiries(dealerId))
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setInquiriesCount(list.length);
        setInquiriesLoading(false);
      })
      .catch(() => {
        setInquiriesCount(0);
        setInquiriesLoading(false);
      });
  };

  const openStatusModal = (statusValues) => {
    setStatusValues(statusValues);
    getCommentData(statusValues.req_id);
    form.setFieldsValue({
      status_change: statusValues.status,
    });
    setStatusModal(true);
  };

  const getCommentData = (req_id) => {
    setModalLoading(true);
    dispatch(InspectionServices.getComments(req_id))
      .then((res) => {
        setCommentData(res);
        setModalLoading(false);
      })
      .catch(() => {
        setModalLoading(false);
      });
  };

  const onFinish = (values) => {
    const previousStatus = statusValues.status;
    const newStatus = values.status_change;
    const isStatusChanged = previousStatus !== newStatus;
    const hasComment = values.comment_status && values.comment_status.trim() !== "";

    if (!isStatusChanged && !hasComment) {
      message.info("No changes detected.");
      return;
    }
    if (!isStatusChanged) {
      delete values.status_change;
    }

    setModalLoading(true);
    values.pId = statusValues.req_id;
    values.client_name = statusValues.client_name;
    values.client_email = statusValues.client_email;
    values.self_name = statusValues.self_name;
    values.self_email = statusValues.self_email;
    values.inspector_id = statusValues.inspector_id;
    values.notify_ins = checkedins ? "yes" : "";
    values.notify_assignee = checkedass ? "yes" : "";

    dispatch(InspectionServices.updateStatusClaim(values))
      .then(() => {
        message.success(isStatusChanged ? "Status updated!" : "Comment added!");
        setStatusModal(false);
        setModalLoading(false);
        const dealerId = currentUser?.id || currentUser?.inspector_id || profileId || 0;
        fetchData(dealerId, page, pageSize);
        form.resetFields();
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || "Failed to update status. Please try again later.";
        message.error(errorMessage);
        setModalLoading(false);
      });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Status form validation failed:", errorInfo);
  };

  const handleCancel = () => {
    setStatusModal(false);
  };

  const viewReport = (record) => {
    const action = record.action;
    const { category, report_id } = action;

    if (category === "Carpet -Basic") {
      navigate("/single-carpet-basic/?report_id=" + report_id);
    } else if (category === "Carpet") {
      navigate("/single-carpet-detailed/?report_id=" + report_id);
    } else if (category === "Hard Surface -Basic") {
      navigate("/single-hardwood-basic/?report_id=" + report_id);
    } else if (category === "LVT") {
      navigate("/single-lvt/?report_id=" + report_id);
    } else if (category === "Hardwood") {
      navigate("/single-hardwood-detailed/?report_id=" + report_id);
    } else if (category === "Tile") {
      navigate("/single-tile-report/?report_id=" + report_id);
    } else if (category === "Laminate") {
      navigate("/single-Laminate/?report_id=" + report_id);
    } else {
      navigate("/single-quick-report/?report_id=" + report_id);
    }
  };

  const handleStatusFilter = (value) => {
    setFilter(value);
    setPage(1);
  };

  const onChangeStartDate = (date, dateString) => {
    if (!dateString) {
      setFilterStartDate("");
      return;
    }
    const start = moment(dateString).format("MM-DD-YYYY");
    setFilterStartDate(start);
    setPage(1);
  };

  const onChangeEndDate = (date, dateString) => {
    if (!dateString) {
      setFilterEndDate("");
      return;
    }
    const end = moment(dateString).format("MM-DD-YYYY");
    setFilterEndDate(end);
    setPage(1);
  };

  const columns = [
    {
      title: "S. No.",
      dataIndex: "index",
      key: "index",
      width: 74,
      align: "center",
      render: (_value, _item, idx) => <>{(page - 1) * pageSize + idx + 1}</>,
    },
    {
      title: "Claim #",
      dataIndex: "claim_number",
      key: "claim_number",
      sorter: (a, b) => (a.claim_number || "").localeCompare(b.claim_number || ""),
      render: (text) => <span style={{ fontWeight: 700, color: '#0a497b' }}>{text || 'N/A'}</span>
    },
    {
      title: "Inspector Name",
      dataIndex: "ins_name",
      key: "Inspector_name",
      sorter: (a, b) => (a.ins_name || "").localeCompare(b.ins_name || ""),
    },
    {
      title: "Inspection Date",
      dataIndex: "ins_date",
      key: "ins_date",
      sorter: (a, b) => (a.ins_date || "").localeCompare(b.ins_date || ""),
    },
    {
      title: "Request Date",
      dataIndex: "date",
      key: "request_date",
      sorter: (a, b) => (a.date || "").localeCompare(b.date || ""),
    },
    {
      title: "Requested",
      dataIndex: "rem_day",
      key: "Requested",
      sorter: (a, b) => (a.rem_day || "").length - (b.rem_day || "").length,
    },
    {
      title: "Status",
      dataIndex: "status_hidden",
      key: "status_hidden",
      render: (_text, record) => (
        <Button className="statusTags" onClick={() => openStatusModal(record.status)}>
          {record.status_hidden}
        </Button>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_i, record) => (
        <div className="actionBtn">
          <IconButton
            icon={<EyeFilled />}
            type="primary"
            tooltip="View"
            text="View"
            handleClick={() =>
              navigate("/single-request/?req_id=" + record.action.req_id)
            }
          />
          {record.action.report_generated === "YES" &&
          record.action.ins_status === "Complete" ? (
            <>
              <IconButton
                icon={<FileOutlined />}
                type="primary"
                tooltip="View Report"
                text="Report"
                handleClick={() => viewReport(record)}
              />
              {record.action.add_attachments === "Yes" && (
                <IconButton
                  icon={<PictureOutlined />}
                  type="primary"
                  tooltip="Photos"
                  text="Photos"
                  handleClick={() => {
                    if (record.action.report_id) {
                      navigate("/attachments/?report_id=" + record.action.report_id)
                    }
                  }}
                />
              )}
              {record.action.invoice_generated === "YES" && (
                <IconButton
                  icon={<BookFilled />}
                  type="primary"
                  tooltip="Invoice"
                  text="Invoice"
                  handleClick={() =>
                    navigate(
                      "/report-invoice/?invoice_id=" +
                        record.action.invoice_id
                    )
                  }
                />
              )}
              <IconButton
                icon={<MailOutlined />}
                type="primary"
                tooltip="Send"
                text="Send"
                handleClick={() =>
                  navigate("/send-email/?req_id=" + record.action.req_id)
                }
              />
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="inspectionWrapper customer-leads-wrapper" style={{ position: 'relative', overflow: 'hidden' }}>
      <div className="inspectionHeader">
        <PagesHeader title="Requested Inspections" />
      </div>
      <div className="buttonSection" style={{ position: 'relative', zIndex: 100 }}>
        <div className="container-xxl">
          <ButtonBar />
        </div>
      </div>
      <div className="inspectionWrapInner" style={{ padding: '20px 0 40px', marginTop: 16, position: 'relative', zIndex: 1 }}>
        <div className="container-xxl">
          
              <Tabs
                activeKey={subTab}
                onChange={(key) => {
                  setSubTab(key);
                  setPage(1);
                }}
                style={{ marginBottom: 15 }}
                type="line"
                className="dashboard-sub-tabs"
              >
                <Tabs.TabPane tab="Active Requests" key="active" />
                <Tabs.TabPane tab="Completed Requests" key="completed" />
                <Tabs.TabPane tab="Archived Requests" key="archived" />
              </Tabs>

              <Card style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                
                {/* Header & Search Filters */}
                <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 15 }}>
                  <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                      📋 Requested Inspections History
                    </h2>
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                      Track all inspection requests sent to Ultimate Inspectors with live status updates
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Input
                      placeholder="Search..."
                      value={searchedText}
                      onChange={(e) => {
                        setSearchedText(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: 200, borderRadius: 8, height: 38 }}
                      allowClear
                    />
                    <Button
                      type={isActive ? "primary" : "default"}
                      onClick={() => setIsActive(!isActive)}
                      style={{ 
                        borderRadius: 8, 
                        height: 38, 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: '6px',
                        fontWeight: 600,
                        ...(isActive ? {
                          backgroundColor: '#0f172a',
                          borderColor: '#0f172a',
                          color: '#ffffff'
                        } : {
                          color: '#0f172a',
                          borderColor: '#cbd5e1'
                        })
                      }}
                    >
                      Filter <DownOutlined style={{ 
                        transform: isActive ? 'rotate(180deg)' : 'none', 
                        transition: 'transform 0.3s',
                        fontSize: '11px'
                      }} />
                    </Button>
                  </div>
                </div>

                {isActive && (
                  <div className="filterSection" style={{ marginBottom: 20 }}>
                    <Card className="filterCard" style={{ background: '#f8fafc', borderRadius: 8 }}>
                      <Row gutter={16} justify="center">
                        <Col span={6}>
                          <div className="formGroup">
                            <Select defaultValue="Status" style={{ width: '100%' }} onChange={handleStatusFilter}>
                              <Option value="all">All</Option>
                              <Option value="Pending">Pending</Option>
                              <Option value="Completed">Complete</Option>
                              <Option value="Draft">Draft</Option>
                              <Option value="Cancelled">Cancelled</Option>
                              <Option value="Archived">Archived</Option>
                              <Option value="More info requested">More info requested</Option>
                            </Select>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div className="formGroup">
                            <DatePicker
                              name="start_date"
                              onChange={onChangeStartDate}
                              placeholder="Date From"
                              format="MM/DD/YYYY"
                              style={{ width: '100%' }}
                            />
                          </div>
                        </Col>
                        <Col span={6}>
                          <div className="formGroup">
                            <DatePicker
                              name="end_date"
                              onChange={onChangeEndDate}
                              placeholder="Date To"
                              format="MM/DD/YYYY"
                              style={{ width: '100%' }}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  </div>
                )}

                {/* Inspections Table */}
                <Table
                  bordered
                  className="inspectionDataTable"
                  tableLayout="auto"
                  scroll={{ x: 1000 }}
                  columns={columns}
                  dataSource={loading ? [] : data}
                  rowKey="inspection_number"
                  pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: total,
                    onChange: (p, ps) => {
                      setPage(p);
                      setPageSize(ps);
                    },
                    position: ['bottomCenter'],
                  }}
                  locale={{
                    emptyText: loading ? (
                      columns.map((u, i) => (
                        <Skeleton.Input
                          key={i}
                          height={50}
                          style={{ marginTop: '2px', height: '50px', padding: '0px' }}
                          block={true}
                          active={true}
                        />
                      ))
                    ) : (
                      <Empty />
                    ),
                  }}
                />
              </Card>
        </div>
      </div>

      {/* Change Status Modal */}
      <Modal
        title="Change Status"
        open={statusModal}
        className="inspectionCategoryModal industryModal"
        style={{ top: 80 }}
        width={720}
        onOk={() => form.submit()}
        footer={[
          <Button
            key="submit"
            htmlType="submit"
            className="submitBtn"
            onClick={() => form.submit()}
            loading={modalLoading}
          >
            Save Changes
          </Button>,
          <Button key="cancel" className="cancelBtn" onClick={handleCancel}>
            Cancel
          </Button>,
        ]}
        onCancel={handleCancel}
      >
        <div className="modalContent">
          <Form
            form={form}
            name="statusForm"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            layout="vertical"
          >
            <Row gutter={16}>
              <Col xs={24} md={24}>
                <Form.Item label="Status" name="status_change">
                  <Select defaultValue="Change Status">
                    <Option value="Pending">Pending</Option>
                    <Option value="Completed">Completed</Option>
                    <Option value="Archived">Archive</Option>
                    <Option value="Delete">Delete</Option>
                    <Option value="Cancelled">Cancelled</Option>
                    <Option value="More info requested">More info requested</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={24}>
                <Form.Item name="notify-ins">
                  <Checkbox onChange={(e) => setCheckedins(e.target.checked)} checked={checkedins}>
                    Notify Inspector
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
            <CommentSection
              loading={modalLoading}
              comments={commentData?.data}
              newCommentFieldName="comment_status"
            />
          </Form>
        </div>
      </Modal>
    </div>
  );
}

export default RequestedInspectionsPage;
